import {
  getAssessmentInterpretationOverride,
  splitAssessmentInterpretationLines,
} from '../utils/interpretationOverride';

export const S_INDEX_BREATHELINK_FLOW_COEFFICIENT = 16;
export const RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT = 5;

export type RespiratoryMuscleTestKey = 'PIMAX' | 'PEMAX' | 'S_INDEX' | 'RESP_ENDURANCE';

export type RespiratoryMusclePatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

export type RespiratoryMuscleProfessionalContext = {
  name?: string | null;
  registration?: string | null;
  role?: string | null;
};

export interface RawBreathData {
  breathNumber: number;
  targetResistance: number | null;
  tins: number | null;
  pressure: number[];
  flow: number[];
}

export interface BreathWaveform {
  breathNumber: number;
  targetResistance: number | null;
  tins: number;
  time: number[];
  pressure: number[];
  flow: number[];
  volume: number[];
  work: number[];
}

export interface PlotWaveform extends BreathWaveform {
  sampleCount: number;
}

export interface BreathMetrics {
  breathNumber: number;
  targetResistance: number | null;
  tins: number;
  peakPressure: number;
  peakFlow: number;
  peakSIndex: number;
  peakVolume: number;
  totalWork: number;
}

export interface PimaxBreathMetrics extends BreathMetrics {
  sustainedPressure1s: number | null;
}

export interface PimaxQualitySummary {
  maneuversAnalyzed: number;
  bestPressure: number;
  secondBestPressure: number | null;
  variationTop2Percent: number | null;
  meetsCriteria: boolean;
  bestBreathNumbers: number[];
  bestSustainedPressure1s: number | null;
}

export interface RespiratoryPressureManualQualitySummary {
  maneuversAnalyzed: number;
  bestPressure: number | null;
  secondBestPressure: number | null;
  variationTop2Percent: number | null;
  meetsCriteria: boolean;
}

export interface PimaxAnalysis {
  waveforms: BreathWaveform[];
  metrics: PimaxBreathMetrics[];
  plotBreaths: PlotWaveform[];
  maxPressure: number;
  maxSustainedPressure1s: number | null;
  quality: PimaxQualitySummary;
}

export interface SIndexAnalysis {
  waveforms: BreathWaveform[];
  metrics: BreathMetrics[];
  plotBreaths: PlotWaveform[];
  maxPressure: number;
}

export interface EnduranceBlockSummary {
  index: number;
  breathRange: string;
  meanPeakFlow: number;
  meanPeakVolume: number;
  meanTotalWork: number;
}

export interface EnduranceAnalysis {
  waveforms: BreathWaveform[];
  metrics: BreathMetrics[];
  plotBreaths: PlotWaveform[];
  blocks: EnduranceBlockSummary[];
  targetResistance: number | null;
  breathsAfterWarmup: number;
  firstBlock: EnduranceBlockSummary | null;
  lastBlock: EnduranceBlockSummary | null;
  reductions: Record<string, number>;
}

export type SIndexReferenceKey =
  | 'reis_2025_hgdul'
  | 'souza_2025_anthropometric'
  | 'souza_2022_height_age'
  | 'kowalski_2024_amc'
  | 'kowalski_2024_wta'
  | 'kowalski_2024_esa';

export type SIndexReferenceResult = {
  key: SIndexReferenceKey;
  label: string;
  predicted: number | null;
  lln: number | null;
  percentPredicted: number | null;
  belowLLN: boolean | null;
  llnDescription: string;
};

export type InterpretationTone = 'good' | 'warn' | 'alert' | 'neutral';

export interface InterpretationResult {
  title: string;
  tone: InterpretationTone;
  lines: string[];
}

export const INTERPRETATION_TONE_CLASS: Record<InterpretationTone, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  alert: 'border-rose-200 bg-rose-50 text-rose-900',
  neutral: 'border-slate-200 bg-slate-50 text-slate-900',
};

const buildEditedInterpretation = (payload: unknown): InterpretationResult | null => {
  const override = getAssessmentInterpretationOverride(payload);
  if (!override) return null;

  return {
    title: 'Interpretação clínica editada',
    tone: 'neutral',
    lines: splitAssessmentInterpretationLines(override),
  };
};

export type RespiratoryTestPosition = 'seated' | 'supine' | 'semi_recumbent' | 'other' | 'unknown';

export interface PimaxTechnique {
  initialVolume: 'RV' | 'TLC' | 'FRC' | 'other' | 'unknown';
  interface: 'mouthpiece' | 'mask' | 'other' | 'unknown';
  device?: string | null;
  lipLeak?: boolean;
  facialSupport?: boolean;
  cooperationLimited?: boolean;
  orthopnea?: boolean;
  notes?: string | null;
}

export interface PimaxPositionProtocol extends PimaxTechnique {
  position: RespiratoryTestPosition;
  angleDegrees?: number | null;
  otherPosition?: string | null;
}

export interface PimaxPositionSeries {
  id: string;
  position: RespiratoryTestPosition;
  angleDegrees?: number | null;
  otherPosition?: string | null;
  technique: PimaxTechnique;
  notPerformedReason?: string | null;
  result: PimaxResultPayload;
}

export const RESPIRATORY_POSITION_LABELS: Record<RespiratoryTestPosition, string> = {
  seated: 'Sentado', supine: 'Decúbito dorsal (supino)', semi_recumbent: 'Semissentado',
  other: 'Outra posição', unknown: 'Posição não informada',
};

export interface PimaxResultPayload {
  report?: { interpretation?: string; [key: string]: unknown };
  measurements: number[];
  rawMeasurements?: number[];
  max: number | null;
  sustained1s?: number | null;
  rawSustained1s?: number | null;
  predicted: number | null;
  percentPredicted: number | null;
  lowerLimit?: number | null;
  belowLowerLimit?: boolean | null;
  notes?: string | null;
  breathelink?: {
    summary: PimaxQualitySummary;
    metrics: PimaxBreathMetrics[];
    plotBreaths: PlotWaveform[];
    selectedBreaths: number[];
    file?: RespiratoryCsvFileMeta;
  } | null;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
  chartImages?: Record<string, string>;
  positionProtocol?: PimaxPositionProtocol;
  positionSeries?: PimaxPositionSeries[];
  primaryPositionSeriesId?: string;
}

export interface PemaxPositionSeries {
  id: string;
  position: RespiratoryTestPosition;
  angleDegrees?: number | null;
  otherPosition?: string | null;
  technique: PimaxTechnique;
  notPerformedReason?: string | null;
  result: PemaxResultPayload;
}

export interface PemaxResultPayload {
  report?: { interpretation?: string; [key: string]: unknown };
  measurements: number[];
  max: number | null;
  sustained1s?: number | null;
  predicted: number | null;
  percentPredicted: number | null;
  lowerLimit?: number | null;
  belowLowerLimit?: boolean | null;
  notes?: string | null;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
  positionProtocol?: PimaxPositionProtocol;
  positionSeries?: PemaxPositionSeries[];
  primaryPositionSeriesId?: string;
}

export type RespiratoryEnduranceModality = 'threshold' | 'hyperpnea' | 'time_trial' | 'custom';
export type RespiratoryEndurancePreset = 'healthy' | 'copd' | 'athlete' | 'global_endurance' | 'custom';
export type RespiratoryEnduranceThresholdProtocol = 'incremental' | 'powerbreathe_incremental' | 'constant';
export type RespiratoryEnduranceReferenceBasis = 'pimax' | 'ppeak' | 'pthmax' | 'absolute';
export type RespiratoryEndurancePacing = 'spontaneous' | 'metronome';

export interface RespiratoryEnduranceProtocol {
  entryMode: 'csv' | 'manual';
  modality: RespiratoryEnduranceModality;
  preset: RespiratoryEndurancePreset;
  purpose?: string | null;
  source?: string | null;
  thresholdProtocol?: RespiratoryEnduranceThresholdProtocol | null;
  referenceBasis?: RespiratoryEnduranceReferenceBasis | null;
  referenceValue?: number | null;
  targetPercent?: number | null;
  targetLoad?: number | null;
  startingPercent?: number | null;
  incrementPercent?: number | null;
  stageDurationSeconds?: number | null;
  pacing?: RespiratoryEndurancePacing | null;
  targetRespiratoryRate?: number | null;
  observedRespiratoryRate?: number | null;
  posture?: string | null;
  familiarization?: boolean | null;
  attemptNumber?: number | null;
  mvv?: number | null;
  targetMvvPercent?: number | null;
  targetVentilation?: number | null;
  isocapnia?: boolean | null;
  targetTidalVolume?: number | null;
  timeTrialDurationSeconds?: number | null;
  timeTrialOutcome?: number | null;
  timeTrialOutcomeUnit?: 'respiracoes' | 'trabalho' | 'ventilacao' | null;
  stopReason?: string | null;
  notes?: string | null;
  borgDyspneaPre?: number | null;
  borgDyspneaPost?: number | null;
  borgFatiguePre?: number | null;
  borgFatiguePost?: number | null;
  spo2Pre?: number | null;
  spo2Post?: number | null;
  heartRatePre?: number | null;
  heartRatePost?: number | null;
}

export interface SIndexResultPayload {
  summary: SIndexEstimationSummary & {
    interpretation?: string[];
  };
  maxPressure: number;
  maxPressureRaw: number;
  referenceEquation: SIndexReferenceKey;
  referenceHandgripKg: number | null;
  predictedSIndex: number | null;
  percentPredicted: number | null;
  llnSIndex: number | null;
  belowLLN: boolean | null;
  estimation: SIndexEstimationSummary['estimation'];
  measurements?: number[];
  metrics: BreathMetrics[];
  plotBreaths: PlotWaveform[];
  selectedBreaths: number[];
  patientAge?: number | null;
  patientSex?: string | null;
  patientHeight?: number | null;
  patientWeight?: number | null;
  file?: RespiratoryCsvFileMeta;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
  chartImages?: Record<string, string>;
}

export interface RespiratoryEnduranceResultPayload {
  summary: {
    tlimSeconds: number;
    tlimFormatted: string;
    targetResistance: number | null;
    breathsAfterWarmup: number;
    firstBlock: EnduranceBlockSummary | null;
    lastBlock: EnduranceBlockSummary | null;
    reductions: Record<string, number>;
  };
  protocol?: RespiratoryEnduranceProtocol | null;
  blocks: EnduranceBlockSummary[];
  metrics: BreathMetrics[];
  plotBreaths: PlotWaveform[];
  file?: RespiratoryCsvFileMeta;
  patientContext?: RespiratoryMusclePatientContext | null;
  professionalContext?: RespiratoryMuscleProfessionalContext | null;
  finishedAt?: string | null;
  chartImages?: Record<string, string>;
}

export const RESPIRATORY_ENDURANCE_PRESETS: Record<RespiratoryEndurancePreset, {
  label: string;
  description: string;
  defaults: Partial<RespiratoryEnduranceProtocol>;
}> = {
  healthy: {
    label: 'Adultos saudáveis',
    description: 'Incremental a partir de 10% da PImax, com incrementos de 10% por minuto; no protocolo constante, usar a carga pico e buscar Tlim de 3 a 10 minutos.',
    defaults: { modality: 'threshold', thresholdProtocol: 'incremental', referenceBasis: 'pimax', startingPercent: 10, incrementPercent: 10, stageDurationSeconds: 60, familiarization: true },
  },
  copd: {
    label: 'DPOC',
    description: 'Carga constante em torno de 50 a 60% da PImax, com alvo operacional de Tlim entre 3 e 7 minutos e mesma carga nas reavaliações.',
    defaults: { modality: 'threshold', thresholdProtocol: 'constant', referenceBasis: 'pimax', targetPercent: 60, familiarization: true },
  },
  athlete: {
    label: 'Atletas',
    description: 'Protocolo incremental ou constante, com familiarização e reprodução rigorosa da carga e do pacing.',
    defaults: { modality: 'threshold', thresholdProtocol: 'incremental', referenceBasis: 'pimax', familiarization: true },
  },
  global_endurance: {
    label: 'Endurance ventilatório global',
    description: 'Hiperventilação isocápnica entre 40 e 70% da ventilação voluntária máxima, buscando falha da tarefa em aproximadamente 8 a 12 minutos.',
    defaults: { modality: 'hyperpnea', targetMvvPercent: 60, isocapnia: true, familiarization: true },
  },
  custom: {
    label: 'Personalizado',
    description: 'Defina modalidade, carga, duração, pacing e critérios de interrupção conforme o protocolo utilizado.',
    defaults: { modality: 'custom' },
  },
};

export const getRespiratoryEnduranceModalityLabel = (modality?: RespiratoryEnduranceModality | null): string => {
  if (modality === 'threshold') return 'Carga limiar';
  if (modality === 'hyperpnea') return 'Hiperventilação isocápnica';
  if (modality === 'time_trial') return 'Tempo fixo';
  return 'Personalizado';
};

export const getRespiratoryEnduranceReferenceLabel = (basis?: RespiratoryEnduranceReferenceBasis | null): string => {
  if (basis === 'pimax') return 'PImax';
  if (basis === 'ppeak') return 'Ppeak';
  if (basis === 'pthmax') return 'Pthmax';
  return 'carga absoluta';
};

export const getRespiratoryEnduranceProtocolLines = (protocol?: RespiratoryEnduranceProtocol | null): string[] => {
  if (!protocol) return [];
  const lines: string[] = [
    `Modalidade: ${getRespiratoryEnduranceModalityLabel(protocol.modality)}.`,
  ];
  const preset = RESPIRATORY_ENDURANCE_PRESETS[protocol.preset];
  if (preset && protocol.preset !== 'custom') lines.push(`População/finalidade: ${preset.label}.`);
  if (protocol.purpose?.trim()) lines.push(`Finalidade: ${protocol.purpose.trim()}.`);
  if (protocol.source?.trim()) lines.push(`Fonte/protocolo: ${protocol.source.trim()}.`);

  if (protocol.modality === 'threshold') {
    const kind = protocol.thresholdProtocol === 'powerbreathe_incremental'
      ? 'incremental PowerBreathe'
      : protocol.thresholdProtocol === 'incremental'
        ? 'incremental completo'
        : 'carga constante';
    const basis = getRespiratoryEnduranceReferenceLabel(protocol.referenceBasis);
    const target = toFiniteNumber(protocol.targetLoad);
    const percent = toFiniteNumber(protocol.targetPercent);
    lines.push(`Protocolo ${kind}, baseado em ${basis}${protocol.thresholdProtocol === 'constant' && percent != null ? ` (${percent.toFixed(0)}%)` : ''}${target != null ? `; ${protocol.thresholdProtocol === 'constant' ? 'carga alvo' : 'carga máxima atingida'} ${target.toFixed(1)} cmH2O` : ''}.`);
    if (protocol.thresholdProtocol === 'incremental' || protocol.thresholdProtocol === 'powerbreathe_incremental') {
      const parts = [
        toFiniteNumber(protocol.startingPercent) != null ? `início em ${toFiniteNumber(protocol.startingPercent)!.toFixed(0)}%` : null,
        toFiniteNumber(protocol.incrementPercent) != null ? `incrementos de ${toFiniteNumber(protocol.incrementPercent)!.toFixed(0)}%` : null,
        toFiniteNumber(protocol.stageDurationSeconds) != null ? `estágios de ${toFiniteNumber(protocol.stageDurationSeconds)!.toFixed(0)} s` : null,
      ].filter(Boolean);
      if (parts.length) lines.push(`Progressão: ${parts.join(', ')}.`);
    }
  }

  if (protocol.modality === 'hyperpnea') {
    const mvv = toFiniteNumber(protocol.mvv);
    const percent = toFiniteNumber(protocol.targetMvvPercent);
    const ventilation = toFiniteNumber(protocol.targetVentilation) ?? (mvv != null && percent != null ? (mvv * percent) / 100 : null);
    lines.push(`Hiperventilação${percent != null ? ` a ${percent.toFixed(0)}% da VVM` : ''}${ventilation != null ? `; ventilação alvo ${ventilation.toFixed(1)} L/min` : ''}${protocol.isocapnia != null ? `; isocapnia ${protocol.isocapnia ? 'controlada' : 'não controlada'}` : ''}.`);
  }

  if (protocol.modality === 'time_trial' || protocol.modality === 'custom') {
    const duration = toFiniteNumber(protocol.timeTrialDurationSeconds);
    const outcome = toFiniteNumber(protocol.timeTrialOutcome);
    if (duration != null) lines.push(`Duração programada: ${formatSecondsToTlim(duration)}.`);
    if (outcome != null) lines.push(`Resultado no tempo fixo: ${outcome}${protocol.timeTrialOutcomeUnit ? ` ${protocol.timeTrialOutcomeUnit}` : ''}.`);
  }

  const execution = [
    protocol.pacing ? `pacing ${protocol.pacing === 'metronome' ? 'por metrônomo' : 'espontâneo'}` : null,
    toFiniteNumber(protocol.targetRespiratoryRate) != null ? `FR alvo ${toFiniteNumber(protocol.targetRespiratoryRate)!.toFixed(0)} irpm` : null,
    toFiniteNumber(protocol.observedRespiratoryRate) != null ? `FR observada ${toFiniteNumber(protocol.observedRespiratoryRate)!.toFixed(0)} irpm` : null,
    protocol.posture?.trim() ? `postura: ${protocol.posture.trim()}` : null,
    protocol.familiarization != null ? `familiarização: ${protocol.familiarization ? 'sim' : 'não'}` : null,
    toFiniteNumber(protocol.attemptNumber) != null ? `tentativa ${toFiniteNumber(protocol.attemptNumber)!.toFixed(0)}` : null,
  ].filter(Boolean);
  if (execution.length) lines.push(`Execução: ${execution.join('; ')}.`);
  const symptoms = [
    toFiniteNumber(protocol.borgDyspneaPre) != null || toFiniteNumber(protocol.borgDyspneaPost) != null
      ? `Borg dispneia: ${toFiniteNumber(protocol.borgDyspneaPre)?.toFixed(0) ?? 'não informado'} para ${toFiniteNumber(protocol.borgDyspneaPost)?.toFixed(0) ?? 'não informado'}`
      : null,
    toFiniteNumber(protocol.borgFatiguePre) != null || toFiniteNumber(protocol.borgFatiguePost) != null
      ? `Borg fadiga: ${toFiniteNumber(protocol.borgFatiguePre)?.toFixed(0) ?? 'não informado'} para ${toFiniteNumber(protocol.borgFatiguePost)?.toFixed(0) ?? 'não informado'}`
      : null,
  ].filter(Boolean);
  if (symptoms.length) lines.push(`Sintomas: ${symptoms.join('; ')}.`);
  const vitalSigns = [
    toFiniteNumber(protocol.spo2Pre) != null || toFiniteNumber(protocol.spo2Post) != null
      ? `SpO2: ${toFiniteNumber(protocol.spo2Pre)?.toFixed(0) ?? 'não informada'}% para ${toFiniteNumber(protocol.spo2Post)?.toFixed(0) ?? 'não informada'}%`
      : null,
    toFiniteNumber(protocol.heartRatePre) != null || toFiniteNumber(protocol.heartRatePost) != null
      ? `FC: ${toFiniteNumber(protocol.heartRatePre)?.toFixed(0) ?? 'não informada'} para ${toFiniteNumber(protocol.heartRatePost)?.toFixed(0) ?? 'não informada'} bpm`
      : null,
  ].filter(Boolean);
  if (vitalSigns.length) lines.push(`Sinais vitais: ${vitalSigns.join('; ')}.`);
  if (protocol.stopReason?.trim()) lines.push(`Interrupção: ${protocol.stopReason.trim()}.`);
  if (protocol.notes?.trim()) lines.push(`Observações do protocolo: ${protocol.notes.trim()}.`);
  return lines;
};

export interface RespiratoryCsvFileMeta {
  name: string;
  size: number;
  importedAt?: string;
}

type ParsedBreathSection = {
  breaths: RawBreathData[];
  targetResistances: (number | null)[];
};

export type WavePoint = {
  time: number;
  pressure: number;
  flow: number;
  volume: number;
  work: number;
};

export type BlockComparisonData = {
  seriesMeta: { key: string; label: string; color: string; blockIndex: number }[];
  pressure: Array<Record<string, number>>;
  flow: Array<Record<string, number>>;
  volume: Array<Record<string, number>>;
  work: Array<Record<string, number>>;
};

type SIndexEstimationMode = 'breathlink_formula' | 'fallback_fixed' | 'calibrated_pimax';

export interface SIndexEstimationSummary {
  maxPressure: number;
  maxPressureRaw: number;
  maxPressureEstimated: number;
  breathsAnalyzed: number;
  referenceEquation: SIndexReferenceKey;
  referenceLabel: string;
  predictedSIndex: number | null;
  percentPredicted: number | null;
  llnSIndex: number | null;
  belowLLN: boolean | null;
  referenceLlnDescription: string;
  referenceHandgripKg: number | null;
  estimation: {
    mode: SIndexEstimationMode;
    kFixed: number;
    kUsed: number;
    flowCoefficient: number;
    formula: string;
    referencePimaxMax: number | null;
  };
}

export interface SIndexReferenceContext {
  referencePimaxMax?: number | null;
  referenceHandgripKg?: number | null;
  selectedReferenceEquation?: SIndexReferenceKey | null;
  patientAge?: number | null;
  patientSex?: string | null;
  patientHeight?: number | null;
  patientWeight?: number | null;
}

const S_INDEX_REFERENCE_OPTIONS: Array<{ key: SIndexReferenceKey; label: string }> = [
  { key: 'souza_2025_anthropometric', label: 'Souza et al. 2025 - sexo, idade, peso e altura' },
  { key: 'reis_2025_hgdul', label: 'Reis et al. 2025 - sexo, idade e handgrip' },
  { key: 'souza_2022_height_age', label: 'Souza ERS 2022 - equação preliminar legada' },
  { key: 'kowalski_2024_amc', label: 'Kowalski et al. 2024 - controles 18-39 (média por sexo)' },
  { key: 'kowalski_2024_wta', label: 'Kowalski et al. 2024 - atletas treinados (média por sexo)' },
  { key: 'kowalski_2024_esa', label: 'Kowalski et al. 2024 - e-sports (média por sexo)' },
];

const DEFAULT_S_INDEX_REFERENCE_KEY: SIndexReferenceKey = 'souza_2025_anthropometric';

const BLOCK_REFERENCE_TARGETS = [1, 5, 10, 20, 25, 30];
const BLOCK_COLOR_PALETTE = ['#0f172a', '#1d4ed8', '#16a34a', '#f97316', '#9333ea', '#dc2626'];

export const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

export const toLocalDateTimeKey = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseCsvNumber = (value?: string | null): number | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCsv = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];

    if (inQuotes) {
      if (char === '"') {
        if (csvText[index + 1] === '"') {
          currentValue += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentValue += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ',') {
      currentRow.push(currentValue);
      currentValue = '';
      continue;
    }
    if (char === '\r' || char === '\n') {
      currentRow.push(currentValue);
      currentValue = '';
      if (char === '\r' && csvText[index + 1] === '\n') index += 1;
      rows.push(currentRow);
      currentRow = [];
      continue;
    }
    currentValue += char;
  }

  currentRow.push(currentValue);
  if (currentRow.length > 1 || currentRow[0] !== '') rows.push(currentRow);
  return rows;
};

const findRowIndex = (rows: string[][], label: string): number => {
  const matcher = label.trim().toLowerCase();
  const index = rows.findIndex((row) => row[0]?.trim().toLowerCase() === matcher);
  if (index === -1) {
    throw new Error(`Não foi possível localizar a linha "${label}" no CSV do BreatheLink.`);
  }
  return index;
};

const extractColumnPositions = (row: string[], label: string): number[] => {
  const matcher = label.toLowerCase();
  const positions: number[] = [];
  row.forEach((cell, index) => {
    if (cell?.trim().toLowerCase() === matcher) positions.push(index);
  });
  if (!positions.length) {
    throw new Error(`Cabeçalho "${label}" não encontrado no CSV do BreatheLink.`);
  }
  return positions;
};

const findRowContainingLabel = (rows: string[][], label: string, startIndex = 0): number => {
  const matcher = label.trim().toLowerCase();
  for (let index = Math.max(0, startIndex); index < rows.length; index += 1) {
    if ((rows[index] || []).some((cell) => cell?.trim().toLowerCase() === matcher)) return index;
  }
  return -1;
};

const collectRawBreaths = ({
  rows,
  columnPositions,
  breathNumbers,
  targetResistances,
  tinsValues,
  pressureHeaderIndex,
}: {
  rows: string[][];
  columnPositions: number[];
  breathNumbers: number[];
  targetResistances: (number | null)[];
  tinsValues: (number | null)[];
  pressureHeaderIndex: number;
}): RawBreathData[] => {
  const dataRows = rows.slice(pressureHeaderIndex + 1);
  const samples = breathNumbers.map(() => ({ pressure: [] as number[], flow: [] as number[] }));

  let blankSequence = 0;
  for (const row of dataRows) {
    let hasValue = false;
    columnPositions.forEach((column, index) => {
      const pressure = column < row.length ? parseCsvNumber(row[column]) : null;
      const flow = column + 1 < row.length ? parseCsvNumber(row[column + 1]) : null;
      if (pressure == null && flow == null) return;
      hasValue = true;
      samples[index].pressure.push(pressure ?? 0);
      samples[index].flow.push(flow ?? 0);
    });

    if (hasValue) {
      blankSequence = 0;
    } else {
      blankSequence += 1;
      if (blankSequence >= 5) break;
    }
  }

  return breathNumbers.map((breathNumber, index) => ({
    breathNumber,
    targetResistance: targetResistances[index] ?? null,
    tins: tinsValues[index] ?? null,
    pressure: samples[index].pressure,
    flow: samples[index].flow,
  }));
};

const parseStandardBreathSection = (rows: string[][]): ParsedBreathSection | null => {
  const breathHeaderIndex = findRowIndex(rows, 'Breath');
  const breathHeaderRow = rows[breathHeaderIndex] || [];
  const columnPositions = extractColumnPositions(breathHeaderRow, 'Breath');
  const breathValuesRow = rows[breathHeaderIndex + 1] || [];

  const breathNumbers: number[] = [];
  const targetResistances: (number | null)[] = [];
  columnPositions.forEach((column) => {
    const number = parseCsvNumber(breathValuesRow[column]);
    if (number == null) return;
    breathNumbers.push(Math.round(number));
    targetResistances.push(column + 1 < breathValuesRow.length ? parseCsvNumber(breathValuesRow[column + 1]) : null);
  });
  if (!breathNumbers.length) return null;

  const tinsIndex = findRowIndex(rows, 'Insp Time(s)');
  const tinsRow = rows[tinsIndex + 1] || [];
  const tinsValues = columnPositions.map((column) => (column < tinsRow.length ? parseCsvNumber(tinsRow[column]) : null));
  const pressureHeaderIndex = findRowIndex(rows, 'Pressure (cmH2O)');

  return {
    breaths: collectRawBreaths({ rows, columnPositions, breathNumbers, targetResistances, tinsValues, pressureHeaderIndex }),
    targetResistances,
  };
};

const parseNeutralBreathSection = (rows: string[][]): ParsedBreathSection | null => {
  const neutralIndex = rows.findIndex((row) => row[0]?.trim().toLowerCase() === '[neutral data]');
  if (neutralIndex === -1) return null;

  const pressureHeaderIndex = findRowContainingLabel(rows, 'Pressure (cmH2O)', neutralIndex);
  const tinsHeaderIndex = findRowContainingLabel(rows, 'To-Insp (s)', neutralIndex);
  const startHeaderIndex = findRowContainingLabel(rows, 'Start Time / Date', neutralIndex);
  if (pressureHeaderIndex === -1 || tinsHeaderIndex === -1 || startHeaderIndex === -1) return null;

  const pressureHeaderRow = rows[pressureHeaderIndex] || [];
  const columnPositions = extractColumnPositions(pressureHeaderRow, 'Pressure (cmH2O)');
  const breathNumbers = columnPositions.map((_, index) => index + 1);
  const startValuesRow = rows[startHeaderIndex + 1] || [];
  const targetResistances = columnPositions.map((column) =>
    column + 1 < startValuesRow.length ? parseCsvNumber(startValuesRow[column + 1]) : null,
  );
  const tinsValuesRow = rows[tinsHeaderIndex + 1] || [];
  const tinsValues = columnPositions.map((column) => (column < tinsValuesRow.length ? parseCsvNumber(tinsValuesRow[column]) : null));

  return {
    breaths: collectRawBreaths({ rows, columnPositions, breathNumbers, targetResistances, tinsValues, pressureHeaderIndex }),
    targetResistances,
  };
};

const countBreathsWithSamples = (section: ParsedBreathSection): number =>
  section.breaths.filter((breath) => breath.pressure.length > 0 && breath.flow.length > 0).length;

export const parseBreathelinkCsv = (csvText: string): { breaths: RawBreathData[]; targetResistances: (number | null)[] } => {
  const rows = parseCsv(csvText);
  if (!rows.length) throw new Error('Arquivo CSV vazio.');

  const candidates: ParsedBreathSection[] = [];
  try {
    const standard = parseStandardBreathSection(rows);
    if (standard) candidates.push(standard);
  } catch {
    // Permite fallback no formato [NEUTRAL DATA].
  }

  try {
    const neutral = parseNeutralBreathSection(rows);
    if (neutral) candidates.push(neutral);
  } catch {
    // CSVs exportados parcialmente ainda podem ser aproveitados.
  }

  if (!candidates.length) throw new Error('Nenhuma respiracao encontrada no CSV.');

  const best = candidates.reduce((selected, current) => {
    const selectedCount = countBreathsWithSamples(selected);
    const currentCount = countBreathsWithSamples(current);
    if (currentCount > selectedCount) return current;
    if (currentCount === selectedCount && current.breaths.length > selected.breaths.length) return current;
    return selected;
  });

  return { breaths: best.breaths, targetResistances: best.targetResistances };
};

const buildWaveform = (raw: RawBreathData): BreathWaveform | null => {
  if (!raw.pressure.length || !raw.flow.length) return null;
  const sampleCount = Math.min(raw.pressure.length, raw.flow.length);
  if (!sampleCount) return null;

  const minDuration = 0.2;
  const hasValidTins = typeof raw.tins === 'number' && raw.tins > 0;
  if (hasValidTins && raw.tins! < minDuration) return null;

  const tins = hasValidTins ? raw.tins! : sampleCount;
  const dt = tins / sampleCount;
  const time: number[] = [];
  const pressure: number[] = [];
  const flow: number[] = [];
  const volume: number[] = [];
  const work: number[] = [];

  let cumulativeVolume = 0;
  let cumulativeWork = 0;
  for (let index = 0; index < sampleCount; index += 1) {
    const pressureValue = raw.pressure[index];
    const flowValue = raw.flow[index];
    time.push(Number((index * dt).toFixed(6)));
    pressure.push(pressureValue);
    flow.push(flowValue);

    const previousFlow = index === 0 ? flowValue : raw.flow[index - 1];
    const deltaVolume = index === 0 ? flowValue * dt : 0.5 * (previousFlow + flowValue) * dt;
    cumulativeVolume += deltaVolume;
    volume.push(cumulativeVolume);

    cumulativeWork += pressureValue * deltaVolume;
    work.push(cumulativeWork);
  }

  return {
    breathNumber: raw.breathNumber,
    targetResistance: raw.targetResistance,
    tins,
    time,
    pressure,
    flow,
    volume,
    work,
  };
};

const buildMetrics = (breaths: BreathWaveform[]): BreathMetrics[] =>
  breaths.map((breath) => {
    const peakPressure = Math.max(...breath.pressure, 0);
    const peakFlow = Math.max(...breath.flow.map((value) => Math.abs(value)), 0);
    const peakSIndex = breath.pressure.reduce((max, pressure, index) => {
      const flow = Math.abs(breath.flow[index] ?? 0);
      return Math.max(max, pressure + S_INDEX_BREATHELINK_FLOW_COEFFICIENT * flow);
    }, 0);
    const peakVolume = Math.max(...breath.volume, 0);
    const totalWork = breath.work[breath.work.length - 1] ?? 0;
    return {
      breathNumber: breath.breathNumber,
      targetResistance: breath.targetResistance,
      tins: breath.tins,
      peakPressure,
      peakFlow,
      peakSIndex,
      peakVolume,
      totalWork,
    };
  });

export const downsamplePlotWaveform = (waveform: BreathWaveform, limit = 120): PlotWaveform => {
  if (waveform.time.length <= limit) return { ...waveform, sampleCount: waveform.time.length };

  const step = Math.ceil(waveform.time.length / limit);
  const indexes: number[] = [];
  for (let index = 0; index < waveform.time.length; index += step) indexes.push(index);
  if (indexes[indexes.length - 1] !== waveform.time.length - 1) indexes.push(waveform.time.length - 1);
  const pick = (series: number[]) => indexes.map((index) => series[index]);

  return {
    ...waveform,
    sampleCount: waveform.time.length,
    time: pick(waveform.time),
    pressure: pick(waveform.pressure),
    flow: pick(waveform.flow),
    volume: pick(waveform.volume),
    work: pick(waveform.work),
  };
};

const computeMaxMovingAverage = (series: number[], windowSamples: number): number => {
  if (!series.length) return 0;
  const size = Math.max(1, Math.min(windowSamples, series.length));
  const prefix = new Array(series.length + 1).fill(0) as number[];
  for (let index = 0; index < series.length; index += 1) prefix[index + 1] = prefix[index] + series[index];

  let best = -Infinity;
  for (let end = size; end <= series.length; end += 1) {
    const average = (prefix[end] - prefix[end - size]) / size;
    if (average > best) best = average;
  }
  return Number.isFinite(best) ? best : 0;
};

export const computePimaxSustained1s = (waveform: BreathWaveform): number | null => {
  const sampleCount = waveform.pressure.length;
  if (!sampleCount) return null;
  const dt = waveform.tins / sampleCount;
  if (!Number.isFinite(dt) || dt <= 0) return null;
  const windowSamples = Math.max(1, Math.round(1 / dt));
  return Number(computeMaxMovingAverage(waveform.pressure, windowSamples).toFixed(2));
};

const buildPimaxMetrics = (breaths: BreathWaveform[]): PimaxBreathMetrics[] => {
  const waveformMap = new Map<number, BreathWaveform>();
  breaths.forEach((waveform) => waveformMap.set(waveform.breathNumber, waveform));
  return buildMetrics(breaths).map((metric) => ({
    ...metric,
    sustainedPressure1s: computePimaxSustained1s(waveformMap.get(metric.breathNumber)!),
  }));
};

const buildPimaxQuality = (metrics: PimaxBreathMetrics[]): Pick<PimaxAnalysis, 'maxPressure' | 'maxSustainedPressure1s' | 'quality'> => {
  const maxPressure = metrics.reduce((max, metric) => Math.max(max, metric.peakPressure), 0);
  const sustainedValues = metrics
    .map((metric) => metric.sustainedPressure1s)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const maxSustainedPressure1s = sustainedValues.length ? Number(Math.max(...sustainedValues).toFixed(2)) : null;
  const sorted = [...metrics].sort((a, b) => b.peakPressure - a.peakPressure);
  const best = sorted[0]?.peakPressure ?? 0;
  const secondBest = sorted[1]?.peakPressure ?? null;
  const variationTop2Percent = secondBest != null && best > 0 ? Number((((best - secondBest) / best) * 100).toFixed(2)) : null;
  const meetsCriteria = metrics.length >= 3
    && variationTop2Percent != null
    && variationTop2Percent <= RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT;

  return {
    maxPressure,
    maxSustainedPressure1s,
    quality: {
      maneuversAnalyzed: metrics.length,
      bestPressure: Number(best.toFixed(2)),
      secondBestPressure: secondBest != null ? Number(secondBest.toFixed(2)) : null,
      variationTop2Percent,
      meetsCriteria,
      bestBreathNumbers: sorted.slice(0, 3).map((metric) => metric.breathNumber),
      bestSustainedPressure1s: maxSustainedPressure1s,
    },
  };
};

export const analyzePimaxCsv = (csvText: string): PimaxAnalysis => {
  const { breaths: rawBreaths } = parseBreathelinkCsv(csvText);
  const normalized = rawBreaths.map((breath) => ({
    ...breath,
    pressure: breath.pressure.map((value) => Math.abs(value)),
  }));
  const waveforms = normalized.map(buildWaveform).filter((item): item is BreathWaveform => Boolean(item));
  if (!waveforms.length) throw new Error('Nenhuma manobra válida encontrada no arquivo de PImax.');

  const metrics = buildPimaxMetrics(waveforms);
  const quality = buildPimaxQuality(metrics);
  return {
    waveforms,
    metrics,
    plotBreaths: waveforms.map((breath) => downsamplePlotWaveform(breath)),
    ...quality,
  };
};

export const analyzeSIndexCsv = (csvText: string): SIndexAnalysis => {
  const { breaths: rawBreaths } = parseBreathelinkCsv(csvText);
  const waveforms = rawBreaths.map(buildWaveform).filter((item): item is BreathWaveform => Boolean(item));
  if (!waveforms.length) throw new Error('Nenhuma respiração válida encontrada no arquivo de S-Index.');
  const metrics = buildMetrics(waveforms);
  return {
    waveforms,
    metrics,
    plotBreaths: waveforms.map((breath) => downsamplePlotWaveform(breath)),
    maxPressure: metrics.reduce((max, metric) => Math.max(max, metric.peakPressure), 0),
  };
};

const computeBlockSummary = (block: BreathWaveform[], index: number): EnduranceBlockSummary => {
  const mean = (values: number[]) => (values.length ? values.reduce((accumulator, current) => accumulator + current, 0) / values.length : 0);
  return {
    index,
    breathRange: block.length ? `${block[0].breathNumber}-${block[block.length - 1].breathNumber}` : '-',
    meanPeakFlow: Number(mean(block.map((breath) => Math.max(...breath.flow.map((value) => Math.abs(value)), 0))).toFixed(4)),
    meanPeakVolume: Number(mean(block.map((breath) => Math.max(...breath.volume, 0))).toFixed(4)),
    meanTotalWork: Number(mean(block.map((breath) => breath.work[breath.work.length - 1] ?? 0)).toFixed(4)),
  };
};

const computeReduction = (first: EnduranceBlockSummary | null, last: EnduranceBlockSummary | null): Record<string, number> => {
  const result: Record<string, number> = {};
  if (!first || !last) return result;
  const metrics: { key: keyof EnduranceBlockSummary; alias: string }[] = [
    { key: 'meanPeakFlow', alias: 'mean_peak_flow' },
    { key: 'meanPeakVolume', alias: 'mean_peak_volume' },
    { key: 'meanTotalWork', alias: 'mean_total_work' },
  ];
  metrics.forEach(({ key, alias }) => {
    const base = first[key] as number;
    const final = last[key] as number;
    const ratio = base ? final / base : 0;
    result[alias] = Number(((1 - ratio) * 100).toFixed(2));
  });
  return result;
};

const findConstantTarget = (targets: (number | null)[], startIndex: number): number | null => {
  for (let index = startIndex; index < targets.length; index += 1) {
    const candidate = targets[index];
    if (candidate == null) continue;
    const rest = targets.slice(index);
    if (rest.every((value) => value == null || Math.abs(value - candidate) < 1e-6)) return candidate;
  }
  return null;
};

export const analyzeEnduranceCsv = (csvText: string): EnduranceAnalysis => {
  const warmupCut = 4;
  const { breaths: rawBreaths, targetResistances } = parseBreathelinkCsv(csvText);
  const waveforms = rawBreaths.map(buildWaveform).filter((item): item is BreathWaveform => Boolean(item));
  if (!waveforms.length) throw new Error('Nenhuma respiração válida encontrada no arquivo de endurance.');

  const processed = waveforms.slice(warmupCut);
  if (!processed.length) throw new Error('Não há respirações suficientes após o aquecimento.');

  const metrics = buildMetrics(processed);
  const blocks: EnduranceBlockSummary[] = [];
  const blockWaveforms: BreathWaveform[][] = [];
  for (let index = 0; index < processed.length; index += 5) {
    const block = processed.slice(index, index + 5);
    if (!block.length) continue;
    blockWaveforms.push(block);
    blocks.push(computeBlockSummary(block, blocks.length + 1));
  }

  const hasValidFlow = (breath: BreathWaveform) => breath.flow.some((value) => Math.abs(value) > 1e-6);
  const reductionSummaries = blockWaveforms.map((breaths, index) => {
    const validBreaths = breaths.filter(hasValidFlow);
    return validBreaths.length ? computeBlockSummary(validBreaths, index + 1) : null;
  });
  const firstBlock = reductionSummaries.find((summary): summary is EnduranceBlockSummary => Boolean(summary)) ?? null;
  let lastBlock: EnduranceBlockSummary | null = null;
  for (let index = reductionSummaries.length - 1; index >= 0; index -= 1) {
    if (reductionSummaries[index]) {
      lastBlock = reductionSummaries[index]!;
      break;
    }
  }

  return {
    waveforms: processed,
    metrics,
    plotBreaths: processed.map((breath) => downsamplePlotWaveform(breath)),
    blocks,
    targetResistance: findConstantTarget(targetResistances, warmupCut),
    breathsAfterWarmup: processed.length,
    firstBlock,
    lastBlock,
    reductions: computeReduction(firstBlock, lastBlock),
  };
};

export const rebuildTrimmedWaveform = (breath: BreathWaveform, trimStart: number, trimEnd: number): BreathWaveform => {
  const totalSamples = breath.pressure.length;
  if (!totalSamples) return breath;
  const startSamples = Math.max(0, Math.round((trimStart / 100) * totalSamples));
  const endSamples = Math.max(0, Math.round((trimEnd / 100) * totalSamples));
  const minSamples = 5;
  const startIndex = Math.min(startSamples, Math.max(0, totalSamples - minSamples));
  const endIndex = Math.max(startIndex + minSamples, totalSamples - endSamples);
  const pressure = breath.pressure.slice(startIndex, endIndex);
  const flow = breath.flow.slice(startIndex, endIndex);
  const sampleCount = pressure.length;
  const ratio = totalSamples > 0 ? sampleCount / totalSamples : 1;
  const tins = Number((breath.tins * ratio).toFixed(6));
  const dt = sampleCount > 0 ? tins / sampleCount : 0;

  const time: number[] = [];
  const volume: number[] = [];
  const work: number[] = [];
  let cumulativeVolume = 0;
  let cumulativeWork = 0;
  for (let index = 0; index < sampleCount; index += 1) {
    time.push(Number((index * dt).toFixed(6)));
    const previousFlow = index === 0 ? flow[index] : flow[index - 1];
    const deltaVolume = index === 0 ? flow[index] * dt : 0.5 * (previousFlow + flow[index]) * dt;
    cumulativeVolume += deltaVolume;
    volume.push(cumulativeVolume);
    cumulativeWork += pressure[index] * deltaVolume;
    work.push(cumulativeWork);
  }

  return {
    breathNumber: breath.breathNumber,
    targetResistance: breath.targetResistance,
    tins,
    time,
    pressure,
    flow,
    volume,
    work,
  };
};

export const applyPimaxBreathEdits = (
  source: PimaxAnalysis,
  edits: Record<number, { trimStart: number; trimEnd: number }>,
): PimaxAnalysis => {
  const waveforms = source.waveforms.map((breath) => {
    const edit = edits[breath.breathNumber];
    if (!edit || (edit.trimStart <= 0 && edit.trimEnd <= 0)) return breath;
    return rebuildTrimmedWaveform(breath, edit.trimStart, edit.trimEnd);
  });
  const metrics = buildPimaxMetrics(waveforms);
  return {
    ...source,
    waveforms,
    metrics,
    plotBreaths: waveforms.map((breath) => downsamplePlotWaveform(breath)),
    ...buildPimaxQuality(metrics),
  };
};

export const applySIndexBreathEdits = (
  source: SIndexAnalysis,
  edits: Record<number, { trimStart: number; trimEnd: number }>,
): SIndexAnalysis => {
  const waveforms = source.waveforms.map((breath) => {
    const edit = edits[breath.breathNumber];
    if (!edit || (edit.trimStart <= 0 && edit.trimEnd <= 0)) return breath;
    return rebuildTrimmedWaveform(breath, edit.trimStart, edit.trimEnd);
  });
  const metrics = buildMetrics(waveforms);
  return {
    ...source,
    waveforms,
    metrics,
    plotBreaths: waveforms.map((breath) => downsamplePlotWaveform(breath)),
    maxPressure: metrics.reduce((max, metric) => Math.max(max, metric.peakPressure), 0),
  };
};

export const sanitizeMetricsForStorage = <T extends BreathMetrics>(metrics: T[]): T[] => {
  const normalize = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(4)) : null;

  return metrics.map((metric) => ({
    ...metric,
    tins: normalize(metric.tins) ?? 0,
    peakPressure: normalize(metric.peakPressure) ?? 0,
    peakFlow: normalize(metric.peakFlow) ?? 0,
    peakSIndex: normalize(metric.peakSIndex) ?? 0,
    peakVolume: normalize(metric.peakVolume) ?? 0,
    totalWork: normalize(metric.totalWork) ?? 0,
    ...(Object.prototype.hasOwnProperty.call(metric, 'sustainedPressure1s')
      ? { sustainedPressure1s: normalize((metric as PimaxBreathMetrics).sustainedPressure1s) }
      : {}),
  })) as T[];
};

export const sanitizePlotBreathsForStorage = (plotBreaths: PlotWaveform[]): PlotWaveform[] =>
  plotBreaths.map((breath) => ({
    breathNumber: breath.breathNumber,
    targetResistance: breath.targetResistance ?? null,
    tins: Number(breath.tins.toFixed(4)),
    sampleCount: breath.sampleCount,
    time: breath.time.map((value) => Number((value ?? 0).toFixed(4))),
    pressure: breath.pressure.map((value) => Number((value ?? 0).toFixed(4))),
    flow: breath.flow.map((value) => Number((value ?? 0).toFixed(4))),
    volume: breath.volume.map((value) => Number((value ?? 0).toFixed(4))),
    work: breath.work.map((value) => Number((value ?? 0).toFixed(4))),
  }));

const buildRespiratoryPressureManualQuality = (measurements: number[]): RespiratoryPressureManualQualitySummary => {
  const sorted = measurements
    .map((measurement) => Math.abs(measurement))
    .sort((a, b) => b - a);
  const bestPressure = sorted[0] ?? null;
  const secondBestPressure = sorted[1] ?? null;
  const variationTop2Percent =
    bestPressure != null && secondBestPressure != null && bestPressure > 0
      ? Number((((bestPressure - secondBestPressure) / bestPressure) * 100).toFixed(2))
      : null;

  return {
    maneuversAnalyzed: measurements.length,
    bestPressure,
    secondBestPressure,
    variationTop2Percent,
    meetsCriteria: measurements.length >= 3
      && variationTop2Percent != null
      && variationTop2Percent <= RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT,
  };
};

export const calculatePredictedPimax = (age?: number | null, sex?: string | null, weightKg?: number | null): number | null => {
  const normalizedAge = toFiniteNumber(age);
  const normalizedSex = normalizeSex(sex);
  if (normalizedAge == null || !normalizedSex) return null;
  const normalizedWeight = toFiniteNumber(weightKg);
  if (normalizedSex === 'M' && normalizedWeight != null) {
    return -0.8 * normalizedAge + 0.48 * normalizedWeight + 119.7;
  }
  return normalizedSex === 'M' ? 155.3 - 0.8 * normalizedAge : 110.5 - 0.49 * normalizedAge;
};

export const normalizeRespiratoryPressureMagnitude = (value: unknown): number | null => {
  const pressure = toFiniteNumber(value);
  return pressure == null ? null : Math.abs(pressure);
};

export const calculatePredictedPemax = (age?: number | null, sex?: string | null): number | null => {
  const normalizedAge = toFiniteNumber(age);
  const normalizedSex = normalizeSex(sex);
  if (normalizedAge == null || !normalizedSex) return null;
  return normalizedSex === 'M' ? 165.3 - 0.81 * normalizedAge : 115.7 - 0.62 * normalizedAge;
};

export const calculatePimaxLowerLimit = (
  age?: number | null,
  sex?: string | null,
  weightKg?: number | null,
): number | null => {
  const predicted = calculatePredictedPimax(age, sex, weightKg);
  const normalizedSex = normalizeSex(sex);
  if (predicted == null || !normalizedSex) return null;
  const residualStandardError = normalizedSex === 'M'
    ? toFiniteNumber(weightKg) != null ? 16.7 : 17.3
    : 9.1;
  return Number((predicted - 1.645 * residualStandardError).toFixed(1));
};

export const calculatePemaxLowerLimit = (age?: number | null, sex?: string | null): number | null => {
  const predicted = calculatePredictedPemax(age, sex);
  const normalizedSex = normalizeSex(sex);
  if (predicted == null || !normalizedSex) return null;
  const residualStandardError = normalizedSex === 'M' ? 15.6 : 11.2;
  return Number((predicted - 1.645 * residualStandardError).toFixed(1));
};

export const getRespiratoryPressureVariationStatus = (
  variationTop2Percent?: number | null,
  maneuversAnalyzed?: number | null,
): string | null => {
  const variation = toFiniteNumber(variationTop2Percent);
  if (variation == null) return null;
  const maneuvers = toFiniteNumber(maneuversAnalyzed);
  if (maneuvers != null && maneuvers < 3) return 'medidas insuficientes';
  return variation <= RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT
    ? 'reprodutível'
    : 'repetição recomendada';
};

export const normalizeSex = (value?: string | null): 'M' | 'F' | null => {
  if (!value) return null;
  const normalized = String(value).trim().toUpperCase();
  if (normalized === 'M' || normalized.startsWith('MASC')) return 'M';
  if (normalized === 'F' || normalized.startsWith('FEM')) return 'F';
  return null;
};

export const getDefaultSIndexReferenceKey = (): SIndexReferenceKey => DEFAULT_S_INDEX_REFERENCE_KEY;

export const getSIndexReferenceOptions = (): Array<{ key: SIndexReferenceKey; label: string }> => [...S_INDEX_REFERENCE_OPTIONS];

export const normalizeSIndexReferenceKey = (value: unknown): SIndexReferenceKey => {
  const raw = typeof value === 'string' ? value.trim() : '';
  return S_INDEX_REFERENCE_OPTIONS.some((option) => option.key === raw)
    ? (raw as SIndexReferenceKey)
    : DEFAULT_S_INDEX_REFERENCE_KEY;
};

export const getSIndexReferenceLabel = (value: unknown): string => {
  const key = normalizeSIndexReferenceKey(value);
  return S_INDEX_REFERENCE_OPTIONS.find((option) => option.key === key)?.label ?? S_INDEX_REFERENCE_OPTIONS[0].label;
};

const buildSIndexReferenceResult = (
  key: SIndexReferenceKey,
  label: string,
  predictedRaw: number | null,
  llnRaw: number | null,
  measuredRaw: number | null,
  llnDescription: string,
): SIndexReferenceResult => {
  const predicted = predictedRaw != null && Number.isFinite(predictedRaw) ? Number(predictedRaw.toFixed(1)) : null;
  const lln = llnRaw != null && Number.isFinite(llnRaw) ? Number(llnRaw.toFixed(1)) : null;
  const measured = measuredRaw != null && Number.isFinite(measuredRaw) ? measuredRaw : null;
  return {
    key,
    label,
    predicted,
    lln,
    percentPredicted: predicted != null && predicted > 0 && measured != null ? Number(((measured / predicted) * 100).toFixed(1)) : null,
    belowLLN: lln != null && measured != null ? measured < lln : null,
    llnDescription,
  };
};

const getReisSIndexLlnBySexAndAge = (sex: 'M' | 'F', age: number): number | null => {
  if (!Number.isFinite(age)) return null;
  if (sex === 'F') {
    if (age >= 20 && age <= 29) return 62.79;
    if (age >= 30 && age <= 39) return 57.05;
    if (age >= 40 && age <= 49) return 56.5;
    if (age >= 50 && age <= 59) return 50.08;
    if (age >= 60 && age <= 65) return 45.24;
    return null;
  }
  if (age >= 20 && age <= 29) return 86.77;
  if (age >= 30 && age <= 39) return 84.18;
  if (age >= 40 && age <= 49) return 74.99;
  if (age >= 50 && age <= 59) return 68.27;
  if (age >= 60 && age <= 65) return 60.8;
  return null;
};

export const computeSIndexPrimaryReference = (
  input: {
    measuredSIndex?: unknown;
    age?: unknown;
    sex?: unknown;
    heightCm?: unknown;
    weightKg?: unknown;
    handgripKg?: unknown;
  },
  selectedReferenceKey?: unknown,
): SIndexReferenceResult | null => {
  const measured = toFiniteNumber(input?.measuredSIndex);
  const age = toFiniteNumber(input?.age);
  const sex = normalizeSex(typeof input?.sex === 'string' ? input.sex : null);
  const heightCm = toFiniteNumber(input?.heightCm);
  const weightKg = toFiniteNumber(input?.weightKg);
  const handgripKg = toFiniteNumber(input?.handgripKg);
  const sexBinary = sex === 'M' ? 1 : sex === 'F' ? 0 : null;

  const reisPred =
    sexBinary != null && age != null && handgripKg != null
      ? 69.72 + 10.765 * sexBinary - 0.211 * age + 0.797 * handgripKg
      : null;
  const reisLlnByAge = sex && age != null ? getReisSIndexLlnBySexAndAge(sex, age) : null;
  const reisLln = reisLlnByAge;
  const reisLlnDescription = reisLlnByAge != null
    ? 'Limite inferior esperado publicado por faixa etária e sexo (Reis et al. 2025)'
    : 'Limite inferior esperado indisponível fora das faixas publicadas (20 a 65 anos)';

  const souzaLegacyPred = sex != null && age != null && heightCm != null
    ? 0.716 * heightCm - (0.348 * age + (sex === 'M' ? 19 : 41))
    : null;
  const souzaPred = sex != null && age != null && weightKg != null && heightCm != null
    ? (sex === 'M' ? -32.3 : -54.0) - 0.39 * age + 0.47 * weightKg + 0.79 * heightCm
    : null;
  const souzaLln = souzaPred != null ? souzaPred - 1.645 * 19.27 : null;
  const kowalskiPred = sex === 'M' ? 128.7 : sex === 'F' ? 70.3 : null;
  const kowalskiWtaPred = sex === 'M' ? 151.6 : sex === 'F' ? 109.6 : null;
  const kowalskiEsaPred = sex === 'M' ? 136.7 : sex === 'F' ? 101.8 : null;

  const all: SIndexReferenceResult[] = [
    buildSIndexReferenceResult(
      'souza_2025_anthropometric',
      getSIndexReferenceLabel('souza_2025_anthropometric'),
      souzaPred,
      souzaLln,
      measured,
      'Limite inferior esperado derivado como predito − 1,645 × RSE 19,27 (Souza et al. 2025)',
    ),
    buildSIndexReferenceResult(
      'souza_2022_height_age',
      getSIndexReferenceLabel('souza_2022_height_age'),
      souzaLegacyPred,
      null,
      measured,
      'Sem limite inferior da normalidade publicado no resumo ERS 2022',
    ),
    buildSIndexReferenceResult('reis_2025_hgdul', getSIndexReferenceLabel('reis_2025_hgdul'), reisPred, reisLln, measured, reisLlnDescription),
    buildSIndexReferenceResult(
      'kowalski_2024_amc',
      getSIndexReferenceLabel('kowalski_2024_amc'),
      kowalskiPred,
      null,
      measured,
      'O estudo apresenta médias e dispersão, sem limite inferior da normalidade validado',
    ),
    buildSIndexReferenceResult(
      'kowalski_2024_wta',
      getSIndexReferenceLabel('kowalski_2024_wta'),
      kowalskiWtaPred,
      null,
      measured,
      'O estudo apresenta médias e dispersão, sem limite inferior da normalidade validado',
    ),
    buildSIndexReferenceResult(
      'kowalski_2024_esa',
      getSIndexReferenceLabel('kowalski_2024_esa'),
      kowalskiEsaPred,
      null,
      measured,
      'O estudo apresenta médias e dispersão, sem limite inferior da normalidade validado',
    ),
  ];

  const normalizedSelected = normalizeSIndexReferenceKey(selectedReferenceKey);
  return all.find((item) => item.key === normalizedSelected) ?? all.find((item) => item.key === DEFAULT_S_INDEX_REFERENCE_KEY) ?? null;
};

const estimateSIndexMetric = (metric: { peakPressure?: number | null; peakFlow?: number | null; peakSIndex?: number | null }): number => {
  const peakSIndex = toFiniteNumber(metric.peakSIndex);
  if (peakSIndex != null) return peakSIndex;
  const peakPressure = toFiniteNumber(metric.peakPressure) ?? 0;
  const peakFlow = toFiniteNumber(metric.peakFlow) ?? 0;
  return peakPressure + S_INDEX_BREATHELINK_FLOW_COEFFICIENT * Math.abs(peakFlow);
};

export const buildSIndexEstimationSummary = (
  metrics: Array<{ peakPressure?: number | null; peakFlow?: number | null; peakSIndex?: number | null }>,
  context: SIndexReferenceContext = {},
): SIndexEstimationSummary => {
  const maxPressureRaw = Number(metrics.reduce((max, metric) => Math.max(max, toFiniteNumber(metric.peakPressure) ?? 0), 0).toFixed(2));
  const maxPressureEstimated = Number(metrics.reduce((max, metric) => Math.max(max, estimateSIndexMetric(metric)), 0).toFixed(2));
  const selectedReferenceEquation = normalizeSIndexReferenceKey(context.selectedReferenceEquation);
  const referenceHandgripKg = toFiniteNumber(context.referenceHandgripKg);
  const referenceResult = computeSIndexPrimaryReference(
    {
      measuredSIndex: maxPressureEstimated,
      age: context.patientAge,
      sex: context.patientSex,
      heightCm: context.patientHeight,
      weightKg: context.patientWeight,
      handgripKg: referenceHandgripKg,
    },
    selectedReferenceEquation,
  );

  return {
    maxPressure: maxPressureEstimated,
    maxPressureRaw,
    maxPressureEstimated,
    breathsAnalyzed: metrics.length,
    referenceEquation: selectedReferenceEquation,
    referenceLabel: getSIndexReferenceLabel(selectedReferenceEquation),
    predictedSIndex: referenceResult?.predicted ?? null,
    percentPredicted: referenceResult?.percentPredicted ?? null,
    llnSIndex: referenceResult?.lln ?? null,
    belowLLN: referenceResult?.belowLLN ?? null,
    referenceLlnDescription: referenceResult?.llnDescription ?? '',
    referenceHandgripKg,
    estimation: {
      mode: 'breathlink_formula',
      kFixed: S_INDEX_BREATHELINK_FLOW_COEFFICIENT,
      kUsed: S_INDEX_BREATHELINK_FLOW_COEFFICIENT,
      flowCoefficient: S_INDEX_BREATHELINK_FLOW_COEFFICIENT,
      formula: `max(Pressao + ${S_INDEX_BREATHELINK_FLOW_COEFFICIENT} x |Fluxo|)`,
      referencePimaxMax: toFiniteNumber(context.referencePimaxMax),
    },
  };
};

export const buildPimaxInterpretation = (options: {
  percentPredicted?: number | null;
  measuredPimax?: number | null;
  predictedPimax?: number | null;
  maneuversAnalyzed?: number | null;
  variationTop2Percent?: number | null;
  lowerLimit?: number | null;
  belowLowerLimit?: boolean | null;
}): InterpretationResult => {
  const percent = toFiniteNumber(options.percentPredicted);
  const measured = toFiniteNumber(options.measuredPimax);
  const predicted = toFiniteNumber(options.predictedPimax);
  const maneuvers = toFiniteNumber(options.maneuversAnalyzed);
  const variation = toFiniteNumber(options.variationTop2Percent);
  const lines: string[] = [];
  const lowerLimit = toFiniteNumber(options.lowerLimit);
  const belowLowerLimit = typeof options.belowLowerLimit === 'boolean'
    ? options.belowLowerLimit
    : lowerLimit != null && measured != null ? measured < lowerLimit : null;
  let title = predicted != null ? 'Limite esperado indisponível' : 'Referência individual indisponível';
  let tone: InterpretationTone = 'neutral';

  if (percent != null && measured != null && predicted != null) {
    lines.push(`PImax de ${measured.toFixed(1)} cmH2O equivale a ${percent.toFixed(1)}% do predito (${predicted.toFixed(1)} cmH2O).`);
  } else {
    lines.push('Sem valor predito válido para comparação individual.');
  }
  if (lowerLimit != null && belowLowerLimit != null) {
    title = belowLowerLimit ? 'Abaixo do limite esperado' : 'Dentro da faixa esperada';
    tone = belowLowerLimit ? 'warn' : 'good';
    lines.push(
      `Limite inferior esperado pela referência de Neder: ${lowerLimit.toFixed(1)} cmH2O; ${belowLowerLimit ? 'o resultado está abaixo desse limite' : 'o resultado está dentro da faixa esperada'}.`,
    );
  } else if (predicted != null) {
    lines.push('O percentual do previsto não permite classificar o resultado sem um limite inferior aplicável.');
  }

  if (maneuvers != null || variation != null) {
    if (maneuvers != null && maneuvers < 3) {
      lines.push('Menos de 3 curvas válidas: confiabilidade reduzida.');
    } else if (variation != null && variation <= RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT) {
      lines.push(`Variação entre as 2 melhores curvas: ${variation.toFixed(2)}% (reprodutível; critério <= ${RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT}%).`);
    } else if (variation != null) {
      lines.push(`Variação entre as 2 melhores curvas: ${variation.toFixed(2)}% (acima do critério de ${RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT}%; considerar repetição).`);
    }
  }

  lines.push('Correlacionar com sintomas, técnica de execução e evolução serial.');
  return { title, tone, lines };
};

export const buildPemaxInterpretation = (options: {
  percentPredicted?: number | null;
  measuredPemax?: number | null;
  predictedPemax?: number | null;
  maneuversAnalyzed?: number | null;
  variationTop2Percent?: number | null;
  lowerLimit?: number | null;
  belowLowerLimit?: boolean | null;
}): InterpretationResult => {
  const percent = toFiniteNumber(options.percentPredicted);
  const measured = toFiniteNumber(options.measuredPemax);
  const predicted = toFiniteNumber(options.predictedPemax);
  const maneuvers = toFiniteNumber(options.maneuversAnalyzed);
  const variation = toFiniteNumber(options.variationTop2Percent);
  const lines: string[] = [];
  const lowerLimit = toFiniteNumber(options.lowerLimit);
  const belowLowerLimit = typeof options.belowLowerLimit === 'boolean'
    ? options.belowLowerLimit
    : lowerLimit != null && measured != null ? measured < lowerLimit : null;
  let title = predicted != null ? 'Limite esperado indisponível' : 'Referência individual indisponível';
  let tone: InterpretationTone = 'neutral';

  if (percent != null && measured != null && predicted != null) {
    lines.push(`PEmax de ${measured.toFixed(1)} cmH2O equivale a ${percent.toFixed(1)}% do predito (${predicted.toFixed(1)} cmH2O).`);
  } else {
    lines.push('Sem valor predito válido para comparação individual.');
  }
  if (lowerLimit != null && belowLowerLimit != null) {
    title = belowLowerLimit ? 'Abaixo do limite esperado' : 'Dentro da faixa esperada';
    tone = belowLowerLimit ? 'warn' : 'good';
    lines.push(
      `Limite inferior esperado pela referência de Neder: ${lowerLimit.toFixed(1)} cmH2O; ${belowLowerLimit ? 'o resultado está abaixo desse limite' : 'o resultado está dentro da faixa esperada'}.`,
    );
  } else if (predicted != null) {
    lines.push('O percentual do previsto não permite classificar o resultado sem um limite inferior aplicável.');
  }

  if (maneuvers != null || variation != null) {
    if (maneuvers != null && maneuvers < 3) {
      lines.push('Menos de 3 manobras válidas: confiabilidade reduzida.');
    } else if (variation != null && variation <= RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT) {
      lines.push(`Variação entre as 2 melhores manobras: ${variation.toFixed(2)}% (reprodutível; critério <= ${RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT}%).`);
    } else if (variation != null) {
      lines.push(`Variação entre as 2 melhores manobras: ${variation.toFixed(2)}% (acima do critério de ${RESPIRATORY_PRESSURE_REPRODUCIBILITY_THRESHOLD_PERCENT}%; considerar repetição).`);
    }
  }

  lines.push('Correlacionar com tosse, técnica de execução e evolução serial.');
  return { title, tone, lines };
};

export const buildSIndexInterpretation = (options: {
  sIndexValue?: number | null;
  breathsAnalyzed?: number | null;
  referencePimaxMax?: number | null;
  predictedSIndex?: number | null;
  percentPredicted?: number | null;
  lln?: number | null;
  belowLLN?: boolean | null;
  referenceLabel?: string | null;
}): InterpretationResult => {
  const sIndex = toFiniteNumber(options.sIndexValue);
  const breaths = toFiniteNumber(options.breathsAnalyzed);
  const referencePimax = toFiniteNumber(options.referencePimaxMax);
  const predicted = toFiniteNumber(options.predictedSIndex);
  const percent = toFiniteNumber(options.percentPredicted);
  const lln = toFiniteNumber(options.lln);
  const belowLLN = typeof options.belowLLN === 'boolean' ? options.belowLLN : lln != null && sIndex != null ? sIndex < lln : null;
  const referenceLabel = typeof options.referenceLabel === 'string' && options.referenceLabel.trim() ? options.referenceLabel.trim() : null;
  const lines: string[] = [];

  if (sIndex == null) {
    return { title: 'Dados insuficientes para interpretar S-Index', tone: 'neutral', lines: ['Confira o arquivo CSV e repita a análise.'] };
  }

  let title = 'Interpretação limitada do S-Index';
  let tone: InterpretationTone = 'neutral';
  if (percent != null && predicted != null) {
    lines.push(`S-Index de ${sIndex.toFixed(1)} cmH2O equivale a ${percent.toFixed(1)}% do predito (${predicted.toFixed(1)} cmH2O${referenceLabel ? `; ${referenceLabel}` : ''}).`);
    if (lln != null && belowLLN != null) {
      title = belowLLN ? 'Abaixo do limite esperado' : 'Dentro da faixa esperada';
      tone = belowLLN ? 'warn' : 'good';
      lines.push(
        `Limite inferior esperado: ${lln.toFixed(1)} cmH2O; ${belowLLN ? 'o resultado está abaixo desse limite' : 'o resultado está dentro da faixa esperada'}.`,
      );
    } else {
      title = 'Limite esperado indisponível';
      lines.push('O percentual do previsto não permite classificar o resultado sem um limite inferior aplicável.');
    }
  } else if (referencePimax != null && referencePimax > 0) {
    const ratio = (sIndex / referencePimax) * 100;
    title = 'Relação entre S-Index e PImax';
    lines.push(`Razão S-Index/PImax: ${ratio.toFixed(1)}% (${sIndex.toFixed(1)} / ${referencePimax.toFixed(1)} cmH2O).`);
  } else {
    lines.push(`Sem dados suficientes para cálculo do predito${referenceLabel ? ` pela referência ${referenceLabel}` : ''}.`);
  }

  if (breaths != null) {
    if (breaths >= 5) lines.push(`Número de respirações analisadas adequado (${breaths}).`);
    else if (breaths >= 3) lines.push(`Número de respirações analisadas limítrofe (${breaths}).`);
    else lines.push(`Poucas respirações analisadas (${breaths}): interpretar com cautela.`);
  }

  lines.push('Correlacionar com PImax, técnica de execução e evolução em reavaliações.');
  return { title, tone, lines };
};

export const buildEnduranceInterpretation = (payload?: RespiratoryEnduranceResultPayload | null): InterpretationResult => {
  const editedInterpretation = buildEditedInterpretation(payload);
  if (editedInterpretation) {
    return editedInterpretation;
  }

  const summary = summarizeRespiratoryEndurancePayload(payload);
  const workReduction = summary.workReduction;
  const lines: string[] = [];
  let title = 'Interpretação limitada do endurance respiratório';
  let tone: InterpretationTone = 'neutral';

  if (workReduction != null) {
    if (workReduction < 15) {
      title = 'Desempenho respiratório sustentado';
      tone = 'good';
    } else if (workReduction < 30) {
      title = 'Queda discreta a moderada de desempenho';
      tone = 'warn';
    } else {
      title = 'Queda acentuada de desempenho respiratório';
      tone = 'alert';
    }
    lines.push(`Redução do trabalho médio entre blocos: ${workReduction.toFixed(1)}%.`);
  } else {
    title = 'Teste de endurance respiratório registrado';
  }

  if (summary.tlimSeconds != null) lines.push(`Tlim registrado: ${formatSecondsToTlim(summary.tlimSeconds)} (${summary.tlimSeconds} s).`);
  if (summary.targetResistance != null) lines.push(`Resistência alvo estimada: ${summary.targetResistance.toFixed(1)} cmH2O.`);
  if (summary.protocol?.modality === 'hyperpnea' && summary.tlimSeconds != null) {
    if (summary.tlimSeconds >= 480 && summary.tlimSeconds <= 720) lines.push('Duração dentro da janela operacional de 8 a 12 minutos descrita para protocolos de hiperventilação sustentada.');
    else lines.push('Em hiperventilação sustentada, 8 a 12 minutos é uma janela operacional frequente; não deve ser aplicada automaticamente aos protocolos com carga limiar.');
  } else if (summary.protocol?.modality === 'threshold' && summary.tlimSeconds != null) {
    if (summary.protocol.preset === 'copd') lines.push('Em DPOC, protocolos constantes costumam ser ajustados para produzir Tlim em torno de 3 a 7 minutos.');
    if (summary.protocol.preset === 'healthy') lines.push('Em adultos saudáveis, protocolos constantes costumam buscar Tlim em torno de 3 a 10 minutos.');
  }
  lines.push('Comparações seriadas exigem a reprodução da modalidade, carga, pacing e demais condições do protocolo.');
  return { title, tone, lines };
};

export const parseTlimToSeconds = (input: string): number | null => {
  const match = input.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const minutes = Number.parseInt(match[1], 10);
  const seconds = Number.parseInt(match[2], 10);
  if (minutes < 0 || seconds < 0 || seconds > 59) return null;
  return minutes * 60 + seconds;
};

export const formatSecondsToTlim = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const interpolateSeriesValue = (times: number[], values: number[], targetTime: number): number => {
  if (!times.length || !values.length) return 0;
  if (targetTime <= times[0]) return values[0];
  const lastIndex = times.length - 1;
  if (targetTime >= times[lastIndex]) return values[lastIndex];
  for (let index = 1; index < times.length; index += 1) {
    if (times[index] >= targetTime) {
      const t1 = times[index - 1];
      const t2 = times[index];
      const v1 = values[index - 1];
      const v2 = values[index];
      const span = t2 - t1;
      if (!span) return v2;
      return v1 + (v2 - v1) * ((targetTime - t1) / span);
    }
  }
  return values[lastIndex];
};

export const buildWavePoints = (waveform?: PlotWaveform | null): WavePoint[] => {
  if (!waveform) return [];
  return waveform.time.map((time, index) => ({
    time: Number(time.toFixed(3)),
    pressure: waveform.pressure[index] ?? 0,
    flow: waveform.flow[index] ?? 0,
    volume: waveform.volume[index] ?? 0,
    work: waveform.work[index] ?? 0,
  }));
};

export const buildOverlappedPressureData = (breaths: PlotWaveform[]): Array<Record<string, number>> => {
  if (!breaths.length) return [];
  const maxLength = Math.max(...breaths.map((breath) => breath.time.length));
  const data: Array<Record<string, number>> = [];
  for (let index = 0; index < maxLength; index += 1) {
    const point: Record<string, number> = { index };
    breaths.forEach((breath) => {
      if (index < breath.time.length) point[`breath_${breath.breathNumber}`] = breath.pressure[index];
    });
    data.push(point);
  }
  return data;
};

export const buildNormalizedPressureComparison = (breaths: PlotWaveform[], selectedBreathNumbers: number[]) => {
  const selectedBreaths = breaths.filter((breath) => selectedBreathNumbers.includes(breath.breathNumber));
  if (!selectedBreaths.length) return null;

  const percentAxis = Array.from({ length: 120 }, (_, index) => Number(((index / 119) * 100).toFixed(1)));
  const palette = ['#2563eb', '#16a34a', '#f97316', '#9333ea', '#dc2626'];
  const seriesMeta = selectedBreaths.map((breath, index) => ({
    key: `b${breath.breathNumber}`,
    label: `Curva ${breath.breathNumber}`,
    color: palette[index % palette.length],
  }));
  const data = percentAxis.map((percent) => {
    const row: Record<string, number> = { percent };
    selectedBreaths.forEach((breath) => {
      row[`b${breath.breathNumber}`] = Number(interpolateSeriesValue(breath.time, breath.pressure, (percent / 100) * breath.tins).toFixed(4));
    });
    return row;
  });
  return { seriesMeta, data };
};

const normalizeBreathRange = (range?: string): { start: number; end: number } | null => {
  if (!range) return null;
  const cleaned = range.replace(/–/g, '-');
  const [startStr, endStr] = cleaned.split('-').map((part) => Number(part.trim()));
  if (!Number.isFinite(startStr)) return null;
  if (!Number.isFinite(endStr)) return { start: startStr, end: startStr };
  return { start: startStr, end: endStr };
};

const selectReferenceBlocks = (blocks: EnduranceBlockSummary[]): number[] => {
  if (!blocks.length) return [];
  const available = blocks.map((block) => block.index).sort((a, b) => a - b);
  const selected: number[] = [];
  BLOCK_REFERENCE_TARGETS.forEach((target) => {
    let candidate = available.find((index) => index === target);
    if (candidate == null) {
      const eligible = available.filter((index) => index <= target);
      candidate = eligible.length ? eligible[eligible.length - 1] : available[0];
    }
    if (candidate != null && !selected.includes(candidate)) selected.push(candidate);
  });
  return selected;
};

const averageWaveformSeries = (group: PlotWaveform[], percentAxis: number[]) => {
  if (!group.length) return null;
  const result = {
    pressure: [] as number[],
    flow: [] as number[],
    volume: [] as number[],
    work: [] as number[],
  };
  percentAxis.forEach((percent) => {
    const fraction = percent / 100;
    const addValue = (key: 'pressure' | 'flow' | 'volume' | 'work') => {
      const sum = group.reduce((accumulator, breath) => accumulator + interpolateSeriesValue(breath.time, breath[key], fraction * breath.tins), 0);
      return Number((sum / group.length).toFixed(4));
    };
    result.pressure.push(addValue('pressure'));
    result.flow.push(addValue('flow'));
    result.volume.push(addValue('volume'));
    result.work.push(addValue('work'));
  });
  return result;
};

export const buildBlockComparisonData = (plotBreaths: PlotWaveform[], blocks: EnduranceBlockSummary[]): BlockComparisonData | null => {
  if (!plotBreaths.length || !blocks.length) return null;
  const percentAxis = Array.from({ length: 120 }, (_, index) => Number(((index / 119) * 100).toFixed(1)));
  const selectedIndexes = selectReferenceBlocks(blocks);
  if (!selectedIndexes.length) return null;

  const sortedBreaths = [...plotBreaths].sort((a, b) => a.breathNumber - b.breathNumber);
  const breathMap = new Map<number, PlotWaveform>();
  sortedBreaths.forEach((breath) => breathMap.set(breath.breathNumber, breath));

  const seriesMeta: BlockComparisonData['seriesMeta'] = [];
  const initializeData = () => percentAxis.map((percent) => ({ percent }));
  const charts = {
    pressure: initializeData(),
    flow: initializeData(),
    volume: initializeData(),
    work: initializeData(),
  };

  selectedIndexes.forEach((blockIndex, index) => {
    const block = blocks.find((item) => item.index === blockIndex);
    if (!block) return;
    const range = normalizeBreathRange(block.breathRange);
    let group: PlotWaveform[] = [];
    if (range) {
      for (let breathNumber = range.start; breathNumber <= range.end; breathNumber += 1) {
        const waveform = breathMap.get(breathNumber);
        if (waveform) group.push(waveform);
      }
    }
    if (!group.length) group = sortedBreaths.slice((blockIndex - 1) * 5, blockIndex * 5);
    const averages = averageWaveformSeries(group, percentAxis);
    if (!averages) return;
    const key = `block_${blockIndex}`;
    seriesMeta.push({ key, label: `Bloco ${blockIndex}`, color: BLOCK_COLOR_PALETTE[index % BLOCK_COLOR_PALETTE.length], blockIndex });
    charts.pressure.forEach((point, pointIndex) => {
      point[key] = averages.pressure[pointIndex];
    });
    charts.flow.forEach((point, pointIndex) => {
      point[key] = averages.flow[pointIndex];
    });
    charts.volume.forEach((point, pointIndex) => {
      point[key] = averages.volume[pointIndex];
    });
    charts.work.forEach((point, pointIndex) => {
      point[key] = averages.work[pointIndex];
    });
  });

  if (!seriesMeta.length) return null;
  return { seriesMeta, ...charts };
};

export const getPimaxPositionLabel = (series: Pick<PimaxPositionSeries, 'position' | 'angleDegrees' | 'otherPosition'>): string => {
  if (series.position === 'other' && series.otherPosition?.trim()) return series.otherPosition.trim();
  const base = RESPIRATORY_POSITION_LABELS[series.position] ?? RESPIRATORY_POSITION_LABELS.unknown;
  return series.position === 'semi_recumbent' && series.angleDegrees != null ? `${base} (${series.angleDegrees}°)` : base;
};

export const getPimaxPositionSeries = (payload?: PimaxResultPayload | null): PimaxPositionSeries[] => {
  if (!payload) return [];
  if (Array.isArray(payload.positionSeries) && payload.positionSeries.length) {
    return payload.positionSeries.filter((series) => series?.result && series.id).map((series) => ({
      ...series,
      technique: series.technique ?? { initialVolume: 'unknown', interface: 'unknown' },
      result: { ...series.result, patientContext: payload.patientContext ?? series.result.patientContext,
        professionalContext: payload.professionalContext ?? series.result.professionalContext,
        positionSeries: undefined, primaryPositionSeriesId: undefined,
        positionProtocol: { ...series.technique, position: series.position, angleDegrees: series.angleDegrees, otherPosition: series.otherPosition } },
    }));
  }
  const protocol = payload.positionProtocol;
  return [{
    id: 'legacy', position: protocol?.position ?? 'unknown', angleDegrees: protocol?.angleDegrees,
    otherPosition: protocol?.otherPosition,
    technique: protocol ?? { initialVolume: 'unknown', interface: 'unknown' },
    result: { ...payload, positionSeries: undefined, primaryPositionSeriesId: undefined },
  }];
};

export const getPimaxPrimarySeries = (payload?: PimaxResultPayload | null): PimaxPositionSeries | null => {
  const series = getPimaxPositionSeries(payload).filter((item) => !item.notPerformedReason);
  return series.find((item) => item.id === payload?.primaryPositionSeriesId) ?? series[0] ?? null;
};

export const isPimaxReferenceApplicable = (payload?: PimaxResultPayload | null): boolean => {
  const protocol = payload?.positionProtocol;
  return !protocol || (protocol.position === 'seated' && protocol.initialVolume === 'RV' && protocol.interface === 'mouthpiece');
};

export const pimaxSeriesAreComparable = (a: PimaxPositionSeries, b: PimaxPositionSeries): boolean => {
  if (a.position === 'unknown' || b.position === 'unknown' || a.position !== b.position) return false;
  if (a.position === 'semi_recumbent' && (a.angleDegrees == null || b.angleDegrees == null || a.angleDegrees !== b.angleDegrees)) return false;
  if (a.position === 'other' && (!a.otherPosition?.trim() || a.otherPosition.trim() !== b.otherPosition?.trim())) return false;
  const x = a.technique, y = b.technique;
  return x.initialVolume !== 'unknown' && y.initialVolume !== 'unknown' && x.initialVolume === y.initialVolume &&
    x.interface !== 'unknown' && y.interface !== 'unknown' && x.interface === y.interface &&
    (!(x.initialVolume === 'other' || x.interface === 'other') || Boolean(x.notes?.trim() && x.notes.trim() === y.notes?.trim())) &&
    Boolean(x.lipLeak) === Boolean(y.lipLeak) && Boolean(x.cooperationLimited) === Boolean(y.cooperationLimited) &&
    Boolean(x.facialSupport) === Boolean(y.facialSupport) && (x.device?.trim() ?? '') === (y.device?.trim() ?? '') &&
    Boolean(a.result.breathelink) === Boolean(b.result.breathelink);
};

export const summarizePimaxPayload = (input?: PimaxResultPayload | null) => {
  const payload = input?.positionSeries?.length ? getPimaxPrimarySeries(input)?.result : input;
  const referenceApplicable = isPimaxReferenceApplicable(payload);
  const rawMeasurements = Array.isArray(payload?.rawMeasurements)
    ? payload!.rawMeasurements
    : payload?.measurements;
  const measurements = Array.isArray(rawMeasurements)
    ? rawMeasurements.map(normalizeRespiratoryPressureMagnitude).filter((value): value is number => value != null)
    : [];
  const max = normalizeRespiratoryPressureMagnitude(payload?.max) ?? (measurements.length ? Math.max(...measurements) : null);
  const sustained1s = normalizeRespiratoryPressureMagnitude(payload?.sustained1s)
    ?? normalizeRespiratoryPressureMagnitude(payload?.breathelink?.summary?.bestSustainedPressure1s);
  const predicted = referenceApplicable ? toFiniteNumber(payload?.predicted)
    ?? calculatePredictedPimax(
      payload?.patientContext?.ageYears ?? null,
      payload?.patientContext?.sex ?? null,
      payload?.patientContext?.weightKg ?? null,
    ) : null;
  const percentPredicted = referenceApplicable ? toFiniteNumber(payload?.percentPredicted) ?? (predicted && max != null ? Number(((max / predicted) * 100).toFixed(1)) : null) : null;
  const lowerLimit = referenceApplicable ? toFiniteNumber(payload?.lowerLimit)
    ?? calculatePimaxLowerLimit(
      payload?.patientContext?.ageYears ?? null,
      payload?.patientContext?.sex ?? null,
      payload?.patientContext?.weightKg ?? null,
    ) : null;
  const belowLowerLimit = lowerLimit != null && max != null
    ? max < lowerLimit
    : referenceApplicable && typeof payload?.belowLowerLimit === 'boolean' ? payload.belowLowerLimit : null;
  const quality = payload?.breathelink?.summary ?? buildRespiratoryPressureManualQuality(measurements);
  const interpretation = buildPimaxInterpretation({
    percentPredicted,
    measuredPimax: max,
    predictedPimax: predicted,
    maneuversAnalyzed: quality.maneuversAnalyzed,
    variationTop2Percent: quality.variationTop2Percent,
    lowerLimit,
    belowLowerLimit,
  });
  const editedInterpretation = buildEditedInterpretation(input) ?? buildEditedInterpretation(payload);

  return {
    measurements,
    max,
    sustained1s,
    predicted,
    percentPredicted,
    lowerLimit,
    belowLowerLimit,
    quality,
    severity: interpretation.title,
    tone: interpretation.tone,
    interpretation: editedInterpretation ?? interpretation,
    referenceApplicable,
    positionLabel: getPimaxPositionLabel(getPimaxPrimarySeries(input) ?? { position: 'unknown' }),
  };
};

export const getPimaxPosturalComparison = (payload?: PimaxResultPayload | null) => {
  const series = getPimaxPositionSeries(payload);
  const seated = series.find((item) => item.position === 'seated' && !item.notPerformedReason);
  const supine = series.find((item) => item.position === 'supine' && !item.notPerformedReason);
  if (!seated || !supine) return null;
  const seatedValue = summarizePimaxPayload(seated.result).max;
  const supineValue = summarizePimaxPayload(supine.result).max;
  const sameTechnique = pimaxSeriesAreComparable({ ...seated, position: 'seated' }, { ...supine, position: 'seated' });
  return { seatedValue, supineValue, sameTechnique,
    reductionPercent: sameTechnique && seatedValue != null && seatedValue > 0 && supineValue != null
      ? (seatedValue - supineValue) / seatedValue * 100 : null,
    interpretation: 'A variação postural da PImax é descritiva; não há ponto de corte diagnóstico universal adotado para esta comparação.',
  };
};

export const getPemaxPositionSeries = (payload?: PemaxResultPayload | null): PemaxPositionSeries[] => {
  if (!payload) return [];
  if (Array.isArray(payload.positionSeries) && payload.positionSeries.length) {
    return payload.positionSeries.filter((series) => Boolean(series?.result && series.id)).map((series) => {
      const technique = series.technique ?? { initialVolume: 'unknown', interface: 'unknown' };
      return {
        ...series,
        technique,
        result: {
          ...series.result,
          patientContext: payload.patientContext ?? series.result.patientContext,
          professionalContext: payload.professionalContext ?? series.result.professionalContext,
          positionSeries: undefined,
          primaryPositionSeriesId: undefined,
          positionProtocol: { ...technique, position: series.position, angleDegrees: series.angleDegrees, otherPosition: series.otherPosition },
        },
      };
    });
  }
  const protocol = payload.positionProtocol;
  return [{
    id: 'legacy',
    position: protocol?.position ?? 'unknown',
    angleDegrees: protocol?.angleDegrees,
    otherPosition: protocol?.otherPosition,
    technique: protocol ?? { initialVolume: 'unknown', interface: 'unknown' },
    result: { ...payload, positionSeries: undefined, primaryPositionSeriesId: undefined },
  }];
};

const pemaxSeriesHasMeasurement = (series: PemaxPositionSeries): boolean =>
  !series.notPerformedReason?.trim() && (normalizeRespiratoryPressureMagnitude(series.result.max) != null
    || (Array.isArray(series.result.measurements) && series.result.measurements.some((value) => normalizeRespiratoryPressureMagnitude(value) != null)));

export const getPemaxPrimarySeries = (payload?: PemaxResultPayload | null): PemaxPositionSeries | null => {
  const series = getPemaxPositionSeries(payload).filter(pemaxSeriesHasMeasurement);
  return series.find((item) => item.id === payload?.primaryPositionSeriesId)
    ?? series.find((item) => item.position === 'seated') ?? series[0] ?? null;
};

/** Neder et al., 1999: seated, maximal expiration from TLC with a mouthpiece. Legacy records retain their saved convention. */
export const isPemaxReferenceApplicable = (payload?: PemaxResultPayload | null): boolean => {
  const protocol = payload?.positionProtocol;
  return !protocol || (protocol.position === 'seated' && protocol.initialVolume === 'TLC' && protocol.interface === 'mouthpiece');
};

export const pemaxSeriesAreComparable = (a: PemaxPositionSeries, b: PemaxPositionSeries): boolean => {
  if (a.notPerformedReason?.trim() || b.notPerformedReason?.trim()) return false;
  if (a.position === 'unknown' || b.position === 'unknown' || a.position !== b.position) return false;
  if (a.position === 'semi_recumbent' && (a.angleDegrees == null || b.angleDegrees == null || a.angleDegrees !== b.angleDegrees)) return false;
  if (a.position === 'other' && (!a.otherPosition?.trim() || a.otherPosition.trim() !== b.otherPosition?.trim())) return false;
  const x = a.technique, y = b.technique;
  return x.initialVolume !== 'unknown' && y.initialVolume !== 'unknown' && x.initialVolume === y.initialVolume
    && x.interface !== 'unknown' && y.interface !== 'unknown' && x.interface === y.interface
    && (!(x.initialVolume === 'other' || x.interface === 'other') || Boolean(x.notes?.trim() && x.notes.trim() === y.notes?.trim()))
    && Boolean(x.lipLeak) === Boolean(y.lipLeak) && Boolean(x.cooperationLimited) === Boolean(y.cooperationLimited)
    && Boolean(x.facialSupport) === Boolean(y.facialSupport) && (x.device?.trim() ?? '') === (y.device?.trim() ?? '');
};

export const validatePemaxPositionSeries = (series: readonly PemaxPositionSeries[]): string[] => {
  const issues: string[] = [];
  if (!series.some(pemaxSeriesHasMeasurement)) issues.push('Registre ao menos uma série com medida válida de PEmax.');
  const ids = new Set<string>();
  series.forEach((item) => {
    if (!item.id || ids.has(item.id)) issues.push('Cada série precisa de uma identificação única.');
    ids.add(item.id);
    if (item.notPerformedReason?.trim()) return;
    if (!pemaxSeriesHasMeasurement(item)) issues.push('Complete, remova ou registre o motivo de não realização das séries sem medida.');
    if (item.position === 'unknown' || !Object.prototype.hasOwnProperty.call(RESPIRATORY_POSITION_LABELS, item.position)) issues.push('Informe a posição de cada série realizada.');
    if (item.position === 'semi_recumbent' && (item.angleDegrees == null || !Number.isFinite(item.angleDegrees) || item.angleDegrees < 0 || item.angleDegrees > 90)) issues.push('Informe uma inclinação de 0 a 90 graus para a posição semissentada.');
    if (item.position === 'other' && !item.otherPosition?.trim()) issues.push('Descreva a outra posição utilizada.');
  });
  return [...new Set(issues)];
};

export const summarizePemaxPayload = (input?: PemaxResultPayload | null) => {
  const payload = input?.positionSeries?.length ? getPemaxPrimarySeries(input)?.result : input;
  const referenceApplicable = isPemaxReferenceApplicable(payload);
  const measurements = Array.isArray(payload?.measurements)
    ? payload!.measurements.map(normalizeRespiratoryPressureMagnitude).filter((value): value is number => value != null)
    : [];
  const quality = buildRespiratoryPressureManualQuality(measurements);
  const max = normalizeRespiratoryPressureMagnitude(payload?.max) ?? quality.bestPressure;
  const sustained1s = normalizeRespiratoryPressureMagnitude(payload?.sustained1s);
  const predicted = referenceApplicable
    ? toFiniteNumber(payload?.predicted) ?? calculatePredictedPemax(payload?.patientContext?.ageYears ?? null, payload?.patientContext?.sex ?? null)
    : null;
  const percentPredicted = referenceApplicable
    ? toFiniteNumber(payload?.percentPredicted) ?? (predicted && max != null ? Number(((max / predicted) * 100).toFixed(1)) : null)
    : null;
  const lowerLimit = referenceApplicable
    ? toFiniteNumber(payload?.lowerLimit) ?? calculatePemaxLowerLimit(payload?.patientContext?.ageYears ?? null, payload?.patientContext?.sex ?? null)
    : null;
  const belowLowerLimit = lowerLimit != null && max != null
    ? max < lowerLimit
    : referenceApplicable && typeof payload?.belowLowerLimit === 'boolean' ? payload.belowLowerLimit : null;
  const interpretation = buildPemaxInterpretation({
    percentPredicted,
    measuredPemax: max,
    predictedPemax: predicted,
    maneuversAnalyzed: quality.maneuversAnalyzed,
    variationTop2Percent: quality.variationTop2Percent,
    lowerLimit,
    belowLowerLimit,
  });
  const editedInterpretation = buildEditedInterpretation(input) ?? buildEditedInterpretation(payload);

  return {
    measurements,
    max,
    sustained1s,
    predicted,
    percentPredicted,
    lowerLimit,
    belowLowerLimit,
    quality,
    severity: interpretation.title,
    tone: interpretation.tone,
    interpretation: editedInterpretation ?? interpretation,
    referenceApplicable,
    positionLabel: getPimaxPositionLabel(getPemaxPrimarySeries(input) ?? { position: 'unknown' }),
  };
};

export const summarizeSIndexPayload = (payload?: SIndexResultPayload | null, referencePimaxMax?: number | null) => {
  const measurements = Array.isArray(payload?.measurements)
    ? payload!.measurements.map((value) => toFiniteNumber(value)).filter((value): value is number => value != null)
    : [];
  const metrics = Array.isArray(payload?.metrics) ? payload!.metrics : [];
  const summary = payload?.summary;
  const breathsAnalyzed = (summary?.breathsAnalyzed ?? metrics.length) || measurements.length;
  const maxPressure =
    toFiniteNumber(summary?.maxPressure) ??
    toFiniteNumber(payload?.maxPressure) ??
    (metrics.length
      ? Number(metrics.reduce((max, metric) => Math.max(max, estimateSIndexMetric(metric)), 0).toFixed(2))
      : measurements.length
        ? Math.max(...measurements)
        : null);
  const selectedReferenceEquation = normalizeSIndexReferenceKey(payload?.referenceEquation ?? summary?.referenceEquation);
  const computedReference = maxPressure != null
    ? computeSIndexPrimaryReference(
        {
          measuredSIndex: maxPressure,
          age: payload?.patientAge ?? payload?.patientContext?.ageYears,
          sex: payload?.patientSex ?? payload?.patientContext?.sex,
          heightCm: payload?.patientHeight ?? payload?.patientContext?.heightCm,
          weightKg: payload?.patientWeight ?? payload?.patientContext?.weightKg,
          handgripKg: payload?.referenceHandgripKg ?? summary?.referenceHandgripKg,
        },
        selectedReferenceEquation,
      )
    : null;
  const predictedSIndex = toFiniteNumber(summary?.predictedSIndex) ?? toFiniteNumber(payload?.predictedSIndex) ?? computedReference?.predicted ?? null;
  const percentPredicted = toFiniteNumber(summary?.percentPredicted) ?? toFiniteNumber(payload?.percentPredicted) ?? computedReference?.percentPredicted ?? null;
  const llnSIndex = toFiniteNumber(summary?.llnSIndex)
    ?? toFiniteNumber(payload?.llnSIndex)
    ?? computedReference?.lln
    ?? null;
  const belowLLN = typeof summary?.belowLLN === 'boolean'
    ? summary.belowLLN
    : typeof payload?.belowLLN === 'boolean'
      ? payload.belowLLN
      : llnSIndex != null
        ? computedReference?.belowLLN ?? null
        : null;
  const referenceLabel = summary?.referenceLabel || getSIndexReferenceLabel(selectedReferenceEquation);
  const interpretation = buildSIndexInterpretation({
    sIndexValue: maxPressure,
    breathsAnalyzed,
    referencePimaxMax: referencePimaxMax ?? summary?.estimation?.referencePimaxMax ?? null,
    predictedSIndex,
    percentPredicted,
    lln: llnSIndex,
    belowLLN,
    referenceLabel,
  });
  const editedInterpretation = buildEditedInterpretation(payload);

  return {
    measurements,
    metrics,
    maxPressure,
    predictedSIndex,
    percentPredicted,
    llnSIndex,
    belowLLN,
    breathsAnalyzed,
    referenceEquation: selectedReferenceEquation,
    referenceLabel,
    severity: interpretation.title,
    tone: interpretation.tone,
    interpretation: editedInterpretation ?? interpretation,
  };
};

export const summarizeRespiratoryEndurancePayload = (payload?: RespiratoryEnduranceResultPayload | null) => {
  const summary = payload?.summary;
  const reductions = summary?.reductions ?? {};
  const workReduction = toFiniteNumber(reductions.mean_total_work);
  return {
    tlimSeconds: toFiniteNumber(summary?.tlimSeconds),
    tlimFormatted: typeof summary?.tlimFormatted === 'string' ? summary.tlimFormatted : summary?.tlimSeconds != null ? formatSecondsToTlim(summary.tlimSeconds) : null,
    targetResistance: toFiniteNumber(summary?.targetResistance),
    breathsAfterWarmup: toFiniteNumber(summary?.breathsAfterWarmup) ?? payload?.metrics?.length ?? 0,
    blockCount: Array.isArray(payload?.blocks) ? payload!.blocks.length : 0,
    reductions,
    flowReduction: toFiniteNumber(reductions.mean_peak_flow),
    volumeReduction: toFiniteNumber(reductions.mean_peak_volume),
    workReduction,
    protocol: payload?.protocol ?? null,
  };
};

export const extractPimaxMaxFromPayload = (payload: unknown): number | null => {
  if (!payload || typeof payload !== 'object') return null;
  const source = payload as Record<string, unknown>;
  const summary = source.summary && typeof source.summary === 'object'
    ? source.summary as Record<string, unknown>
    : {};
  const breathelink = source.breathelink && typeof source.breathelink === 'object'
    ? source.breathelink as Record<string, unknown>
    : {};
  const breathelinkSummary = breathelink.summary && typeof breathelink.summary === 'object'
    ? breathelink.summary as Record<string, unknown>
    : {};
  const pimax = source.pimax && typeof source.pimax === 'object'
    ? source.pimax as Record<string, unknown>
    : {};
  const candidates = [source.max, summary.bestPressure, breathelinkSummary.bestPressure, pimax.max];
  for (const candidate of candidates) {
    const parsed = toFiniteNumber(candidate);
    if (parsed != null) return parsed;
  }
  return null;
};

export const extractHandgripMaxFromPayload = (payload: unknown): number | null => {
  if (!payload || typeof payload !== 'object') return null;
  const source = payload as Record<string, unknown>;
  const right = source.right && typeof source.right === 'object'
    ? source.right as Record<string, unknown>
    : {};
  const left = source.left && typeof source.left === 'object'
    ? source.left as Record<string, unknown>
    : {};
  const candidates = [source.bestOverall, right.best, left.best];
  for (const candidate of candidates) {
    const parsed = toFiniteNumber(candidate);
    if (parsed != null) return parsed;
  }
  return null;
};

export const getRespiratorySeverityTone = (tone?: InterpretationTone | null): string => {
  if (tone === 'good') return 'bg-emerald-100 text-emerald-700';
  if (tone === 'warn') return 'bg-amber-100 text-amber-700';
  if (tone === 'alert') return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};
